import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload, X } from 'lucide-react';
import { Job } from '../types/Job';
import { addJob, updateJob, getJobById, getUniqueCountries } from '../utils/storage';
import { uploadImage } from '../lib/supabase';

const DEFAULT_COUNTRIES = ['Cruceros Fluviales'];

const JobForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = id && id !== 'new';
  const [uploadingImage, setUploadingImage] = useState(false);
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [showNewCountryInput, setShowNewCountryInput] = useState(false);

  const [formData, setFormData] = useState<Omit<Job, 'id'>>({
    title: '',
    company: 'TrabajoFuera',
    location: '',
    country: '',
    type: 'full-time',
    salary: '',
    description: '',
    requirements: [''],
    benefits: [''],
    datePosted: new Date().toISOString().split('T')[0],
    contactEmail: 'info@trabajofuera.com',
    isActive: true,
    headerImage: ''
  });

  useEffect(() => {
    const loadData = async () => {
      const countries = await getUniqueCountries();
      const allCountries = DEFAULT_COUNTRIES.concat(countries.filter(c => !DEFAULT_COUNTRIES.includes(c)));
      setAvailableCountries(allCountries.sort());

      if (isEdit) {
        const job = await getJobById(id);
        if (job) {
          setFormData({
            ...job,
            datePosted: job.datePosted.split('T')[0]
          });
          if (job.country && !countries.includes(job.country)) {
            setAvailableCountries(prev => [...prev, job.country].sort());
          }
        } else {
          navigate('/admin');
        }
      }
    };
    loadData();
  }, [id, isEdit, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleArrayChange = (field: 'requirements' | 'benefits', index: number, value: string) => {
    const newArray = [...(formData[field] || [])];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayItem = (field: 'requirements' | 'benefits') => {
    setFormData({ ...formData, [field]: [...(formData[field] || []), ''] });
  };

  const removeArrayItem = (field: 'requirements' | 'benefits', index: number) => {
    const newArray = (formData[field] || []).filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecciona un archivo de imagen válido.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen debe ser menor a 5MB.');
      return;
    }

    setUploadingImage(true);
    try {
      const imageUrl = await uploadImage(file);
      if (imageUrl) {
        setFormData({ ...formData, headerImage: imageUrl });
        alert('Imagen subida correctamente');
      } else {
        alert('Error al subir la imagen. Por favor, revisa la consola del navegador para más detalles.');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al subir la imagen. Por favor, revisa la consola del navegador para más detalles.');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeHeaderImage = () => {
    setFormData({ ...formData, headerImage: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const jobData: Job = {
        id: isEdit ? id : Date.now().toString(),
        ...formData,
        company: 'TrabajoFuera',
        contactEmail: 'info@trabajofuera.com',
        requirements: formData.requirements.filter(r => r.trim() !== ''),
        benefits: formData.benefits?.filter(b => b.trim() !== '') || []
      };

      if (isEdit) {
        await updateJob(id, jobData);
      } else {
        await addJob(jobData);
      }

      navigate('/admin');
    } catch (error) {
      console.error('Error saving job:', error);
      alert('Error al guardar la oferta. Por favor, intenta de nuevo.');
    }
  };

  return (
    <div>
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-16">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center text-white hover:underline mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al panel
          </button>
          <h1 className="text-4xl font-bold">
            {isEdit ? 'Editar Oferta de Trabajo' : 'Nueva Oferta de Trabajo'}
          </h1>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título del Puesto *
                  </label>
                  <select
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  >
                    <option value="">Selecciona un puesto</option>
                    <option value="Camarero/a">Camarero/a</option>
                    <option value="Chef">Chef</option>
                    <option value="Cocinero/a">Cocinero/a</option>
                    <option value="Ayudante de Cocina">Ayudante de Cocina</option>
                    <option value="Limpieza">Limpieza</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ciudad *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    placeholder="ej. Amsterdam, Dublín, Cork"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    País *
                  </label>
                  {showNewCountryInput ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        placeholder="Nombre del país"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewCountryInput(false);
                          setFormData({ ...formData, country: '' });
                        }}
                        className="px-3 py-2 text-gray-600 hover:text-gray-800"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <select
                      name="country"
                      value={formData.country}
                      onChange={(e) => {
                        if (e.target.value === '__new__') {
                          setShowNewCountryInput(true);
                          setFormData({ ...formData, country: '' });
                        } else {
                          handleChange(e);
                        }
                      }}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    >
                      <option value="">Selecciona un país</option>
                      {availableCountries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                      <option value="__new__">+ Añadir nuevo país...</option>
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Empleo *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  >
                    <option value="full-time">Tiempo Completo</option>
                    <option value="part-time">Tiempo Parcial</option>
                    <option value="contract">Contrato</option>
                    <option value="temporary">Temporal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Salario
                  </label>
                  <input
                    type="text"
                    name="salary"
                    value={formData.salary}
                    onChange={handleChange}
                    placeholder="ej. 30,000 - 40,000 EUR/año"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Imagen de Cabecera
                  </label>
                  {formData.headerImage ? (
                    <div className="space-y-2">
                      <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-gray-300">
                        <img
                          src={formData.headerImage}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={removeHeaderImage}
                          className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Haz clic en la X para eliminar la imagen</p>
                    </div>
                  ) : (
                    <div>
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload className="w-8 h-8 mb-2 text-gray-400" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Haz clic para subir</span> o arrastra y suelta
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG o WEBP (MAX. 5MB)</p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                        />
                      </label>
                      {uploadingImage && (
                        <p className="text-sm text-blue-600 mt-2">Subiendo imagen...</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Si no se sube una imagen, se usará una por defecto</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción del Puesto *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                />
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requisitos *
                </label>
                {formData.requirements.map((req, index) => (
                  <div key={index} className="flex mb-2">
                    <input
                      type="text"
                      value={req}
                      onChange={(e) => handleArrayChange('requirements', index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      placeholder="Ingresa un requisito"
                    />
                    {formData.requirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('requirements', index)}
                        className="ml-2 px-4 py-2 text-red-600 hover:text-red-800"
                      >
                        Eliminar
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('requirements')}
                  className="mt-2 text-red-600 hover:text-red-800 font-medium"
                >
                  + Añadir requisito
                </button>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beneficios
                </label>
                {formData.benefits?.map((benefit, index) => (
                  <div key={index} className="flex mb-2">
                    <input
                      type="text"
                      value={benefit}
                      onChange={(e) => handleArrayChange('benefits', index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      placeholder="Ingresa un beneficio"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('benefits', index)}
                      className="ml-2 px-4 py-2 text-red-600 hover:text-red-800"
                    >
                      Eliminar
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('benefits')}
                  className="mt-2 text-red-600 hover:text-red-800 font-medium"
                >
                  + Añadir beneficio
                </button>
              </div>

              <div className="mt-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Publicar oferta inmediatamente
                  </span>
                </label>
              </div>

              <div className="mt-8 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  className="px-6 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition flex items-center space-x-2"
                >
                  <Save className="w-5 h-5" />
                  <span>{isEdit ? 'Guardar Cambios' : 'Crear Oferta'}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default JobForm;